import React from 'react';
import { Phone, Calendar, Zap, Clock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useCMS } from '../hooks/useCMS';
import { useSlotSettings } from '../hooks/useSlotSettings';

const Hero = ({ onBookingClick }) => {
  const { get } = useCMS('hero');
  const { get: getContact } = useCMS('contact');
  const { slotCapacity } = useSlotSettings();
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background Placeholder */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          {/* Placeholder for video - using image overlay */}
          <img
            src="https://images.pexels.com/photos/7019598/pexels-photo-7019598.jpeg"
            alt="Sand-tec Workshop"
            className="w-full h-full object-cover opacity-40"
          />
          {/* Video overlay effect */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        </div>
      </div>

      {/* Animated Speed Lines */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-yellow-400 to-transparent opacity-20 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-3/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-30 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-red-600/20 border border-red-600/50 rounded-full px-6 py-2 mb-8 backdrop-blur-sm">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-semibold text-white">{slotCapacity} parallelle bookbare plasser</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
            <span className="block text-white">{get('hero_title_line1', 'Presisjon som')}</span>
            <span className="block bg-gradient-to-r from-red-600 via-red-500 to-yellow-400 bg-clip-text text-transparent">
              {get('hero_title_line2', 'Formula 1')}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-2xl mx-auto">
            {get('hero_subtitle', 'Profesjonell EU-kontroll og service for tungbil, tilhenger og personbil')}
          </p>

          <p className="text-lg text-gray-400 mb-12">
            {get('hero_description', 'Moderne verksted med kapasitet for 6 store tilhengere samtidig')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <Button
              size="lg"
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg px-8 py-6 rounded-xl shadow-2xl hover:shadow-red-500/50 transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
              onClick={onBookingClick}
            >
              <Calendar className="w-5 h-5 mr-2" />
              {get('hero_cta_book', 'Book time nå')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold text-lg px-8 py-6 rounded-xl backdrop-blur-sm transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
              onClick={() => window.location.href = `tel:${getContact('contact_phone', '')}`}
            >
              <Phone className="w-5 h-5 mr-2" />
              {getContact('contact_phone', '')}
            </Button>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 border border-white/20">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-semibold">{get('hero_feature_1', 'Rask Service')}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 border border-white/20">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">{get('hero_feature_2', 'Sertifiserte Teknikere')}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 border border-white/20">
              <Zap className="w-5 h-5 text-red-400" />
              <span className="text-white font-semibold">{get('hero_feature_3', 'Moderne Utstyr')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent z-10"></div>
    </section>
  );
};

export default Hero;